import {
    usePortfolioReasoning,
    useUserPortfolioReasoning,
    useGenerateReasoning,
    useGenerateUserPortfolioReasoning,
} from "@repo/api";
import type { PreferredLanguage } from "@repo/api";
import { useTranslation } from "@repo/core";

/**
 * "template" - delivered strategy, addressed by a composite {userId}_{universe}_{period}_{modelId} key
 * "user"     - the user's own portfolio, addressed by its model UUID (owner resolved from the token)
 */
export type ReasoningVariant = "template" | "user";

interface UseReasoningSourceParams {
    variant: ReasoningVariant;
    userId?: string;
    strategyId?: string;
    modelId?: string;
    period?: string;
    rebalanceDate?: string;
}

/**
 * Picks the reasoning endpoints matching the strategy variant, so ReasoningChat
 * doesn't need to know how either kind of portfolio is keyed on the backend.
 */
export const useReasoningSource = ({ variant, userId, strategyId, modelId, period, rebalanceDate }: UseReasoningSourceParams) => {
    const isUser = variant === "user";
    const { i18n } = useTranslation();
    // i18n.language may be a regional tag (e.g. "cs-CZ"); the backend accepts only "en" | "cs"
    const language: PreferredLanguage = i18n.language?.toLowerCase().startsWith("cs") ? "cs" : "en";

    // Both queries are always called (Rules of Hooks); the unused one is disabled by an empty key
    const templateQuery = usePortfolioReasoning(isUser ? "" : strategyId ?? "", modelId ?? "", period ?? "", rebalanceDate ?? "");
    const userQuery = useUserPortfolioReasoning(isUser ? modelId ?? "" : "", rebalanceDate ?? "");
    const generateTemplateMutation = useGenerateReasoning();
    const generateUserMutation = useGenerateUserPortfolioReasoning();

    const query = isUser ? userQuery : templateQuery;

    /**
     * The key WebSocketMessageHandler derives from the matching "dataPrepared" broadcast
     * target. Derived from props only, so every ReasoningChat instance showing the same
     * portfolio/date (e.g. the mobile and desktop copies) resolves the same pending state.
     */
    const pendingKey = (() => {
        if (!modelId || !rebalanceDate) return undefined;

        // target: "reasoning/{userId}#{modelId}/{date}"
        if (isUser) return `${modelId}#${rebalanceDate}`;

        // target: "reasoning/{userId}#{strategyId}#{period}#{modelId}/{date}"
        // Note: the strategyId/userId props are backend composite keys, not bare values
        // (e.g. strategyId = "admin_sp500_M", userId = "admin#sp500#3Y"),
        // so the bare userId/universe must be derived rather than used directly.
        const strategyPK = (strategyId ?? "").replace(/_\d*[MYWQD]$/, '');
        const bareUserId = (userId ?? "").split("#")[0];
        const universe = bareUserId && strategyPK.startsWith(`${bareUserId}_`)
            ? strategyPK.slice(bareUserId.length + 1)
            : strategyPK;
        return `${bareUserId}#${universe}#${period}#${modelId}#${rebalanceDate}`;
    })();

    /** Triggers generation; completion is announced by a "dataPrepared" broadcast matching `pendingKey`. */
    const generate = async (): Promise<void> => {
        if (!modelId || !rebalanceDate) return;

        if (isUser) {
            await generateUserMutation.mutateAsync({ modelId, rebalanceDate, language });
            return;
        }

        // Build composite key: remove period suffix and insert actual period, then append modelId
        const compositePK = `${(strategyId ?? "").replace(/_\d*[MYWQD]$/, '')}_${period}_${modelId}`;
        await generateTemplateMutation.mutateAsync({ modelId: compositePK, rebalanceDate, language });
    };

    return {
        reasoning: query.data,
        isLoading: query.isLoading,
        error: query.error,
        generate,
        pendingKey,
        isGeneratePending: isUser ? generateUserMutation.isPending : generateTemplateMutation.isPending,
        // User portfolios send only the model UUID; the backend adds the owner from the token
        chatTarget: isUser ? { modelId } : { strategyId: userId, modelId },
        language,
    };
};
